import { useRef, useState } from 'react';
import { useParticipante } from '@/contexts/ParticipanteContext';
import { useEmpresa } from '@/contexts/EmpresaContext';
import { TicketModal } from '@/components/support/TicketModal';

interface ResgateItem {
  id: string;
  item: string;
  tipo: string;
  pontos: number;
  data: string;
  status: string;
  valor?: string;
  chavePix?: string;
  rastreio?: string;
  transportadora?: string;
  previsaoEntrega?: string;
  dataEntrega?: string;
  enderecoEntrega?: string;
  codigo?: string;
  canaisEnvio?: string[];
  acesso?: string;
  validade?: string;
  local?: string;
  dataUtilizacao?: string;
}

interface ComprovanteResgateModalProps {
  resgate: ResgateItem | null;
  onClose: () => void;
}

const statusConfig: Record<string, { cor: string; texto: string; icone: string; bgColor: string }> = {
  entregue:   { cor: 'text-emerald-700', texto: 'Entregue',    icone: 'ri-checkbox-circle-fill', bgColor: 'bg-emerald-50 border-emerald-200' },
  concluido:  { cor: 'text-emerald-700', texto: 'Concluído',   icone: 'ri-checkbox-circle-fill', bgColor: 'bg-emerald-50 border-emerald-200' },
  em_transito:{ cor: 'text-sky-700',     texto: 'Em Trânsito', icone: 'ri-truck-line',            bgColor: 'bg-sky-50 border-sky-200' },
  ativo:      { cor: 'text-violet-700',  texto: 'Ativo',       icone: 'ri-play-circle-fill',      bgColor: 'bg-violet-50 border-violet-200' },
  utilizado:  { cor: 'text-slate-600',   texto: 'Utilizado',   icone: 'ri-check-double-line',     bgColor: 'bg-slate-50 border-slate-200' },
  pendente:   { cor: 'text-amber-700',   texto: 'Pendente',    icone: 'ri-time-line',             bgColor: 'bg-amber-50 border-amber-200' },
};

const tipoLabel: Record<string, string> = {
  financeiro:  'Financeiro',
  produto:     'Produto',
  voucher:     'Voucher',
  curso:       'Curso',
  experiencia: 'Experiência',
};

const tipoIcone: Record<string, string> = {
  financeiro:  'ri-bank-card-line',
  produto:     'ri-shopping-bag-line',
  voucher:     'ri-coupon-line',
  curso:       'ri-graduation-cap-line',
  experiencia: 'ri-compass-3-line',
};

const LOGO_EMPRESA_URL = 'https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png';

export default function ComprovanteResgateModal({ resgate, onClose }: ComprovanteResgateModalProps) {
  const { participante, campanhaAtual } = useParticipante();
  const { empresaSelecionada } = useEmpresa();
  const printRef = useRef<HTMLDivElement>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>(LOGO_EMPRESA_URL);
  const [logoError, setLogoError] = useState(false);

  if (!resgate) return null;

  const status = statusConfig[resgate.status] ?? statusConfig.pendente;
  const dataFormatada = new Date(resgate.data).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
  const horaFormatada = new Date(resgate.data).toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit'
  });
  const protocolo = `RES-${resgate.id.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)}-${new Date(resgate.data).getFullYear()}`;

  const ticketSubject = `Problema no resgate — Protocolo ${protocolo}`;
  const ticketDescription = `Protocolo: ${protocolo}
Item resgatado: ${resgate.item}
Tipo: ${tipoLabel[resgate.tipo] ?? resgate.tipo}
Data: ${dataFormatada} às ${horaFormatada}
Pontos debitados: ${resgate.pontos.toLocaleString('pt-BR')} pontos
Campanha: ${campanhaAtual?.nome ?? '—'}
Participante: ${participante?.nome ?? '—'}
${resgate.valor ? `Valor: ${resgate.valor}` : ''}
${resgate.chavePix ? `Chave PIX: ${resgate.chavePix}` : ''}
${resgate.rastreio ? `Rastreio: ${resgate.rastreio}` : ''}
${resgate.codigo ? `Código: ${resgate.codigo}` : ''}

Descreva aqui o problema encontrado nesta transação:
`;

  const handleCompartilharWhatsApp = () => {
    const nomeEmpresa = empresaSelecionada?.nome ?? 'getPoints';
    const mensagem = `*Comprovante de Resgate — ${nomeEmpresa}*\n\n` +
      `📋 *Protocolo:* ${protocolo}\n` +
      `🎁 *Item:* ${resgate.item}\n` +
      `📅 *Data:* ${dataFormatada} às ${horaFormatada}\n` +
      `🏆 *Pontos debitados:* ${resgate.pontos.toLocaleString('pt-BR')} pontos\n` +
      `✅ *Status:* ${status.texto}\n` +
      (resgate.valor ? `💰 *Valor:* ${resgate.valor}\n` : '') +
      (resgate.chavePix ? `🔑 *Chave PIX:* ${resgate.chavePix}\n` : '') +
      (resgate.rastreio ? `📦 *Rastreio:* ${resgate.rastreio}\n` : '') +
      (resgate.codigo ? `🎟️ *Código:* ${resgate.codigo}\n` : '') +
      `\n_Plataforma de Incentivos ${nomeEmpresa}_`;

    const url = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleImprimir = () => {
    const detalhesEspecificos = (() => {
      if (resgate.tipo === 'financeiro') {
        return `
          <hr class="divider">
          <div class="section">
            <div class="section-title">Detalhes Financeiros</div>
            <div class="detail-box">
              ${resgate.valor ? `<div class="detail-row"><span class="key">Valor Transferido</span><span class="val">${resgate.valor}</span></div>` : ''}
              ${resgate.chavePix ? `<div class="detail-row"><span class="key">Chave PIX</span><span class="val">${resgate.chavePix}</span></div>` : ''}
            </div>
          </div>`;
      }
      if (resgate.tipo === 'produto') {
        return `
          <hr class="divider">
          <div class="section">
            <div class="section-title">Detalhes da Entrega</div>
            <div class="detail-box">
              ${resgate.rastreio ? `<div class="detail-row"><span class="key">Código de Rastreio</span><span class="val">${resgate.rastreio}</span></div>` : ''}
              ${resgate.transportadora ? `<div class="detail-row"><span class="key">Transportadora</span><span class="val">${resgate.transportadora}</span></div>` : ''}
              ${resgate.previsaoEntrega ? `<div class="detail-row"><span class="key">Previsão de Entrega</span><span class="val">${new Date(resgate.previsaoEntrega).toLocaleDateString('pt-BR')}</span></div>` : ''}
              ${resgate.enderecoEntrega ? `<div class="detail-row"><span class="key">Endereço</span><span class="val">${resgate.enderecoEntrega}</span></div>` : ''}
            </div>
          </div>`;
      }
      if (resgate.tipo === 'voucher') {
        return `
          <hr class="divider">
          <div class="section">
            <div class="section-title">Código do Voucher</div>
            <div class="detail-box">
              ${resgate.codigo ? `<div class="detail-row"><span class="key">Código</span><span class="val" style="font-family:monospace;letter-spacing:0.1em">${resgate.codigo}</span></div>` : ''}
              ${resgate.canaisEnvio ? `<div class="detail-row"><span class="key">Enviado via</span><span class="val">${resgate.canaisEnvio.join(', ')}</span></div>` : ''}
            </div>
          </div>`;
      }
      if (resgate.tipo === 'curso') {
        return `
          <hr class="divider">
          <div class="section">
            <div class="section-title">Acesso ao Curso</div>
            <div class="detail-box">
              ${resgate.acesso ? `<div class="detail-row"><span class="key">Link de Acesso</span><span class="val">${resgate.acesso}</span></div>` : ''}
              ${resgate.validade ? `<div class="detail-row"><span class="key">Válido até</span><span class="val">${new Date(resgate.validade).toLocaleDateString('pt-BR')}</span></div>` : ''}
            </div>
          </div>`;
      }
      if (resgate.tipo === 'experiencia') {
        return `
          <hr class="divider">
          <div class="section">
            <div class="section-title">Detalhes da Experiência</div>
            <div class="detail-box">
              ${resgate.local ? `<div class="detail-row"><span class="key">Local</span><span class="val">${resgate.local}</span></div>` : ''}
              ${resgate.dataUtilizacao ? `<div class="detail-row"><span class="key">Data de Utilização</span><span class="val">${new Date(resgate.dataUtilizacao).toLocaleDateString('pt-BR')}</span></div>` : ''}
            </div>
          </div>`;
      }
      return '';
    })();

    const nomeEmpresa = empresaSelecionada?.nome ?? 'getPoints';
    const logoImgTag = logoUrl && !logoError
      ? `<img src="${logoUrl}" alt="${nomeEmpresa}" style="height:64px;max-width:200px;object-fit:contain;display:block;" />`
      : `<div style="font-size:22px;font-weight:800;color:#f97316;">${nomeEmpresa}</div>`;

    const htmlConteudo = `
      <div class="header">
        <div class="brand-block">
          ${logoImgTag}
          <div style="font-size:12px;color:#64748b;margin-top:4px;">Plataforma de Incentivos</div>
        </div>
        <div class="protocolo">Protocolo<strong>${protocolo}</strong></div>
      </div>
      <div class="status-badge">&#10003; ${status.texto}</div>
      <div class="section">
        <div class="section-title">Dados do Participante</div>
        <div class="info-grid">
          <div class="info-item"><label>Nome</label><span>${participante?.nome ?? 'Participante'}</span></div>
          <div class="info-item"><label>Campanha</label><span>${campanhaAtual?.nome ?? '—'}</span></div>
        </div>
      </div>
      <hr class="divider">
      <div class="section">
        <div class="section-title">Dados do Resgate</div>
        <div class="info-grid">
          <div class="info-item"><label>Item Resgatado</label><span>${resgate.item}</span></div>
          <div class="info-item"><label>Tipo</label><span>${tipoLabel[resgate.tipo] ?? resgate.tipo}</span></div>
          <div class="info-item"><label>Data</label><span>${dataFormatada}</span></div>
          <div class="info-item"><label>Horário</label><span>${horaFormatada}</span></div>
        </div>
      </div>
      <hr class="divider">
      <div class="pontos-box">
        <div>
          <div class="label">Pontos Debitados</div>
          <div class="sub">Saldo utilizado neste resgate</div>
        </div>
        <div style="text-align:right">
          <div class="valor">${resgate.pontos.toLocaleString('pt-BR')}</div>
          <div class="sub">pontos</div>
        </div>
      </div>
      ${detalhesEspecificos}
      <div class="footer">
        <p>Este comprovante é válido como documento oficial da transação realizada na plataforma de incentivos <strong>${nomeEmpresa}</strong>.</p>
        <p>Em caso de dúvidas, entre em contato com o suporte informando o protocolo <strong>${protocolo}</strong>.</p>
        <div class="watermark">Gerado em ${new Date().toLocaleString('pt-BR')} &bull; ${nomeEmpresa} &bull; Plataforma de Incentivos</div>
      </div>
    `;

    const estilos = `
      <style id="print-comprovante-style">
        @media print {
          body > *:not(#print-comprovante-overlay) { display: none !important; }
          #print-comprovante-overlay { display: block !important; position: static !important; background: #fff !important; }
          #print-comprovante-overlay * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        #print-comprovante-overlay {
          display: none;
          position: fixed; inset: 0; z-index: 99999;
          background: #fff; padding: 32px;
          font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b;
          overflow: auto;
        }
        #print-comprovante-overlay .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #f97316; padding-bottom: 20px; margin-bottom: 24px; }
        #print-comprovante-overlay .brand-block { display: flex; flex-direction: column; justify-content: center; }
        #print-comprovante-overlay .protocolo { font-size: 12px; color: #64748b; text-align: right; }
        #print-comprovante-overlay .protocolo strong { display: block; font-size: 16px; color: #1e293b; }
        #print-comprovante-overlay .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 999px; font-size: 13px; font-weight: 600; background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; margin-bottom: 20px; }
        #print-comprovante-overlay .section { margin-bottom: 20px; }
        #print-comprovante-overlay .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; margin-bottom: 10px; }
        #print-comprovante-overlay .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        #print-comprovante-overlay .info-item label { font-size: 11px; color: #64748b; display: block; margin-bottom: 2px; }
        #print-comprovante-overlay .info-item span { font-size: 14px; font-weight: 600; color: #1e293b; }
        #print-comprovante-overlay .divider { border: none; border-top: 1px solid #e2e8f0; margin: 16px 0; }
        #print-comprovante-overlay .pontos-box { background: #fff7ed; border: 2px solid #fed7aa; border-radius: 12px; padding: 16px; display: flex; justify-content: space-between; align-items: center; }
        #print-comprovante-overlay .pontos-box .label { font-size: 13px; color: #9a3412; }
        #print-comprovante-overlay .pontos-box .valor { font-size: 28px; font-weight: 800; color: #ea580c; }
        #print-comprovante-overlay .pontos-box .sub { font-size: 12px; color: #c2410c; }
        #print-comprovante-overlay .detail-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; }
        #print-comprovante-overlay .detail-row { display: flex; justify-content: space-between; font-size: 13px; padding: 4px 0; }
        #print-comprovante-overlay .detail-row .key { color: #64748b; }
        #print-comprovante-overlay .detail-row .val { font-weight: 600; color: #1e293b; }
        #print-comprovante-overlay .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #94a3b8; }
        #print-comprovante-overlay .watermark { font-size: 10px; color: #cbd5e1; margin-top: 8px; }
      </style>
    `;

    // Injeta estilos se ainda não existirem
    if (!document.getElementById('print-comprovante-style')) {
      document.head.insertAdjacentHTML('beforeend', estilos);
    }

    // Cria ou reutiliza overlay
    let overlay = document.getElementById('print-comprovante-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'print-comprovante-overlay';
      document.body.appendChild(overlay);
    }
    overlay.innerHTML = htmlConteudo;
    overlay.style.display = 'block';

    // Dispara impressão e remove overlay após
    window.print();
    overlay.style.display = 'none';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-file-text-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Comprovante de Resgate</h2>
              <p className="text-orange-100 text-xs font-mono">{protocolo}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        {/* Conteúdo scrollável */}
        <div ref={printRef} className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* Status */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${status.bgColor}`}>
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <i className={`${status.icone} text-xl ${status.cor}`}></i>
            </div>
            <div>
              <p className={`font-bold text-sm ${status.cor}`}>{status.texto}</p>
              <p className="text-xs text-slate-500">Status atual do resgate</p>
            </div>
          </div>

          {/* Dados do participante */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Dados do Participante</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-xs text-slate-500 mb-0.5">Nome</p>
                <p className="text-sm font-semibold text-slate-800">{participante?.nome ?? 'Participante'}</p>
              </div>
              <div className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-xs text-slate-500 mb-0.5">Campanha</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{campanhaAtual?.nome ?? '—'}</p>
              </div>
            </div>
          </div>

          {/* Dados do resgate */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Dados do Resgate</p>
            <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 flex-shrink-0">
                  <i className={`${tipoIcone[resgate.tipo] ?? 'ri-gift-line'} text-orange-600`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">Item Resgatado</p>
                  <p className="text-sm font-semibold text-slate-800 truncate">{resgate.item}</p>
                </div>
                <span className="text-xs font-medium px-2 py-1 bg-orange-100 text-orange-700 rounded-full whitespace-nowrap">
                  {tipoLabel[resgate.tipo] ?? resgate.tipo}
                </span>
              </div>
              <div className="grid grid-cols-2 divide-x divide-slate-200">
                <div className="px-4 py-3">
                  <p className="text-xs text-slate-500 mb-0.5">Data</p>
                  <p className="text-sm font-semibold text-slate-800">{dataFormatada}</p>
                </div>
                <div className="px-4 py-3">
                  <p className="text-xs text-slate-500 mb-0.5">Horário</p>
                  <p className="text-sm font-semibold text-slate-800">{horaFormatada}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pontos */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-orange-800">Pontos Debitados</p>
              <p className="text-xs text-orange-600 mt-0.5">Saldo utilizado neste resgate</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-extrabold text-orange-600">{resgate.pontos.toLocaleString('pt-BR')}</p>
              <p className="text-xs text-orange-500 font-medium">pontos</p>
            </div>
          </div>

          {/* Detalhes específicos por tipo */}
          {resgate.tipo === 'financeiro' && (resgate.valor || resgate.chavePix) && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Detalhes Financeiros</p>
              <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                {resgate.valor && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                      </div>
                      <span className="text-sm text-slate-600">Valor Transferido</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-600">{resgate.valor}</span>
                  </div>
                )}
                {resgate.chavePix && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-key-2-line text-orange-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Chave PIX</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800 font-mono">{resgate.chavePix}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {resgate.tipo === 'produto' && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Detalhes da Entrega</p>
              <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                {resgate.rastreio && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-barcode-line text-slate-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Código de Rastreio</span>
                    </div>
                    <span className="text-sm font-bold text-slate-800 font-mono">{resgate.rastreio}</span>
                  </div>
                )}
                {resgate.transportadora && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-truck-line text-sky-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Transportadora</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800">{resgate.transportadora}</span>
                  </div>
                )}
                {resgate.previsaoEntrega && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-check-line text-teal-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Previsão de Entrega</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800">
                      {new Date(resgate.previsaoEntrega).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                {resgate.enderecoEntrega && (
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-map-pin-line text-rose-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Endereço de Entrega</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 pl-8">{resgate.enderecoEntrega}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {resgate.tipo === 'voucher' && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Código do Voucher</p>
              <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                {resgate.codigo && (
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500 mb-2">Código</p>
                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <i className="ri-coupon-line text-orange-500"></i>
                      <span className="font-mono text-sm font-bold text-slate-800 flex-1 tracking-widest">{resgate.codigo}</span>
                      <button
                        onClick={() => navigator.clipboard?.writeText(resgate.codigo ?? '')}
                        className="text-slate-400 hover:text-orange-500 transition-colors cursor-pointer"
                        title="Copiar código"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                )}
                {resgate.canaisEnvio && resgate.canaisEnvio.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-send-plane-line text-orange-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Enviado via</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800 capitalize">{resgate.canaisEnvio.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {resgate.tipo === 'curso' && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Acesso ao Curso</p>
              <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                {resgate.acesso && (
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500 mb-1">Link de Acesso</p>
                    <a
                      href={resgate.acesso}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="text-sm font-semibold text-orange-600 hover:underline break-all"
                    >
                      {resgate.acesso}
                    </a>
                  </div>
                )}
                {resgate.validade && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-timer-line text-violet-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Válido até</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800">
                      {new Date(resgate.validade).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {resgate.tipo === 'experiencia' && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Detalhes da Experiência</p>
              <div className="bg-slate-50 rounded-xl divide-y divide-slate-200">
                {resgate.local && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-map-pin-line text-rose-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Local</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800">{resgate.local}</span>
                  </div>
                )}
                {resgate.dataUtilizacao && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-event-line text-teal-500"></i>
                      </div>
                      <span className="text-sm text-slate-600">Data de Utilização</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800">
                      {new Date(resgate.dataUtilizacao).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rodapé do comprovante */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-line text-teal-600"></i>
              </div>
              <p className="text-xs font-semibold text-slate-600">Autenticidade</p>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Este comprovante é válido como documento oficial da transação realizada na plataforma getPoints.
              Em caso de dúvidas, entre em contato com o suporte informando o protocolo{' '}
              <strong className="text-slate-700 font-mono">{protocolo}</strong>.
            </p>
          </div>

          {/* Banner relatar problema */}
          <button
            onClick={() => setShowTicketModal(true)}
            className="w-full flex items-center gap-3 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition-colors cursor-pointer group"
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-100 group-hover:bg-rose-200 transition-colors flex-shrink-0">
              <i className="ri-error-warning-line text-rose-600 text-lg"></i>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-rose-700">Encontrou um problema?</p>
              <p className="text-xs text-rose-500">Abrir chamado de suporte com dados desta transação</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-rose-400 text-lg"></i>
            </div>
          </button>
        </div>

        {/* Ações */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="py-2.5 px-4 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            Fechar
          </button>
          <button
            onClick={handleCompartilharWhatsApp}
            className="flex-1 py-2.5 bg-[#25D366] text-white rounded-xl font-semibold hover:bg-[#1ebe5d] transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Compartilhar
          </button>
          <button
            onClick={handleImprimir}
            className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line text-base"></i>
            Imprimir / PDF
          </button>
        </div>
      </div>

      {/* Modal de suporte pré-preenchido */}
      {showTicketModal && (
        <TicketModal
          isOpen={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          defaultCategory="financeiro"
          defaultPriority="alta"
          defaultSubject={ticketSubject}
          defaultDescription={ticketDescription}
        />
      )}
    </div>
  );
}
